import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import classes from "./CreateContract.module.less";
import { Button } from "~/common/components/Button/Button";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MaterialInput } from "~/common/components/materialInput/MaterialInput";
import DateInput from "~/common/components/DateInput";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { IconType } from "~/common/components/icon/IconType";
import { IconClickable, IconClickableType } from "~/common/components/icon/IconClickable";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import { Icon } from "~/common/components/icon/Icon";
import {
  AutosuggestProductInput,
  useFetchProductAutosuggest,
} from "~/QuickEntryPopup/components/AutosuggestProductInput/AutosuggestProductInput";
import { Trans, useTranslation } from "react-i18next";
import { requestAvailabilityDataSingle } from "~/common/api/availabilityService";
import type { AvailabilityInfo } from "~/api/pna/AvailabilityInfo";
import type { B2BUnitDataDTO } from "~/Cart/dto/CheckoutViewDTO";
import Modal from "~/common/components/Modal";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { LinkButton } from "~/common/components/Link";
import classNames from "classnames";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CONTRACT_OVERVIEW_PAGE } from "~/utils/url/staticUrls";
import { getCsrfValue } from "~/api/ApiHelper";
import { isDateInvalidForDelivery } from "~/utils/dateUtils";
import { AlertTitle, TextField } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";

interface CreateContractProps {
  addresses: string;
  soldTo: string;
}

interface FormValues {
  contractNumber: string;
  startDate: Date | null;
  endDate: Date | null;
  totalQuantity: number | null;
  numberOfDeliveries: number | null;
  productId: string;
  packagingOption: number | null;
  unloadingPoint: string | null;
  comment: string;
  shippingAddressParentUnit: string;
  shippingAddressId: string;
  shippingCompany: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingAddressPostalCode: string;
  shippingAddressProvince: string;
  shippingAddressCountry: string;
}

enum ErrorType {
  QUANTITY = "QUANTITY_ERROR",
  DATE = "DATE_ERROR",
}

const Tile = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <div className={classes.tile}>
      <h3 className={classes.tileTitle}>{title}</h3>
      {children}
    </div>
  );
};

const DynamicErrorMessage = ({ errors }: { errors: FieldErrors<FormValues> }) => {
  const { t } = useTranslation();
  let title;
  let message;
  if (Object.keys(errors).length === 0) {
    return null;
  } else if (errors.packagingOption?.message === ErrorType.QUANTITY) {
    title = t("newContract.quantityErrorTitle");
    message = t("newContract.quantityErrorMessage");
  } else if (errors.startDate?.message === ErrorType.DATE || errors.endDate?.message === ErrorType.DATE) {
    title = t("newContract.dateErrorTitle");
    message = t("newContract.dateErrorMessage");
  } else {
    message = t("newContract.missingFieldsErrorMessage");
  }
  return (
    <Alert severity={"error"}>
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </Alert>
  );
};

const RequestContractPage = ({ addresses, soldTo }: CreateContractProps) => {
  const parsedAddresses: AddressDTO[] = useMemo(() => JSON.parse(addresses), [addresses]);
  const parsedUnloadingPoints = useMemo(() => {
    const parsedUnits: B2BUnitDataDTO = JSON.parse(soldTo);
    return (
      parsedUnits.children &&
      parsedUnits.children.flatMap((child) => {
        if (child.unloadingPoints) {
          return child.unloadingPoints.map((point) => ({
            uid: child.uid,
            id: point.id,
            name: point.name,
          }));
        } else {
          return [];
        }
      })
    );
  }, [soldTo]);

  const DEFAULT_ADDRESS = parsedAddresses.find((address) => address.defaultAddress);

  const EMPTY_VALUES = {
    contractNumber: "",
    startDate: null,
    endDate: null,
    totalQuantity: null,
    numberOfDeliveries: null,
    productId: "",
    packagingOption: null,
    unloadingPoint: null,
    comment: "",
    shippingAddressParentUnit: DEFAULT_ADDRESS?.parentUnit ?? "",
    shippingAddressId: DEFAULT_ADDRESS?.id ?? "",
    shippingCompany: DEFAULT_ADDRESS?.companyName ?? "",
    shippingAddressLine1: DEFAULT_ADDRESS?.line1 ?? "",
    shippingAddressLine2: DEFAULT_ADDRESS?.line2 ?? "",
    shippingAddressPostalCode: DEFAULT_ADDRESS?.postalCode ?? "",
    shippingAddressProvince: DEFAULT_ADDRESS?.province ?? "",
    shippingAddressCountry: DEFAULT_ADDRESS?.country.name ?? "",
  };

  const { t } = useTranslation();
  const { register, watch, setValue, handleSubmit, control, getValues, formState, reset, trigger } =
    useForm<FormValues>({
      defaultValues: EMPTY_VALUES,
      mode: "all",
    });

  const { inputProps, products, selectedProduct, packCode } = useFetchProductAutosuggest(watch("productId") ?? "");
  const [packageOptions, setPackageOptions] = useState<AvailabilityInfo>();
  const { val: addressModal, setTrue: showAddressModal, setFalse: hideAddressModal } = useBooleanState(false);

  useEffect(() => {
    if (selectedProduct?.code) {
      requestAvailabilityDataSingle({ productCode: selectedProduct.code })
        .then((res) => {
          setPackageOptions(res);
        })
        .catch(() => setPackageOptions(undefined));
    }
  }, [selectedProduct]);

  const submitHandler = handleSubmit((values) => {
    const apiUrl = "/my-account/new-contract/submit";
    const formattedData = {
      contractNumber: values.contractNumber,
      quantity: values.totalQuantity,
      numberOfDeliveries: values.numberOfDeliveries,
      packageCode: packageOptions?.packagingInformation.find((entry) => entry.packSize === values.packagingOption)
        ?.packCode,
      startDate: values.startDate,
      endDate: values.endDate,
      materialNumber: selectedProduct?.sapMaterialNumber,
      address: values.shippingAddressId,
      unloadingPoint: values.unloadingPoint,
      comment: values.comment,
    };
    fetch(resolveApiUrl(apiUrl), {
      body: JSON.stringify(formattedData),
      cache: "no-cache",
      credentials: "same-origin",
      method: "POST",
      mode: "cors",
      headers: {
        dataType: "json",
        "content-type": "application/json",
        CSRFToken: getCsrfValue(),
      },
    }).then(() => redirectToUrl(`${CONTRACT_OVERVIEW_PAGE}?success`));
  });

  const changeSelectedAddress = (address: AddressDTO) => {
    setValue("shippingAddressParentUnit", address.parentUnit ?? "");
    setValue("shippingAddressId", address.id);
    setValue("shippingCompany", address.companyName);
    setValue("shippingAddressLine1", address.line1!);
    setValue("shippingAddressLine2", address.line2!);
    setValue("shippingAddressPostalCode", address.postalCode);
    setValue("shippingAddressProvince", address.province);
    setValue("shippingAddressCountry", address.country.name);
    if (address.id === watch("shippingAddressId")) {
      setValue("unloadingPoint", null);
    }
  };

  const filteredUnloadingPoints = parsedUnloadingPoints?.filter(
    (point) => point.uid === watch("shippingAddressParentUnit"),
  );

  const AddressInformation = ({
    id,
    company,
    line1,
    line2,
    postalCode,
    province,
    country,
    onClick,
    className,
  }: {
    id: string;
    company: string;
    line1: string;
    line2: string;
    postalCode: string;
    province: string;
    country: string;
    onClick?: () => void;
    className?: string;
  }) => {
    const isSelected = watch("shippingAddressId") === id && classes.selected;
    return (
      <LinkButton
        onClick={onClick}
        className={classNames(className, classes.addressInfo, isSelected && classes.selected)}
      >
        <div className="d-flex align-items-center">
          <span className={classes.selectedPartner}>{company ?? ""}</span>
          {isSelected && <Icon className={classes.selectedIcon} type={IconType.CheckCircleOutline} />}
        </div>
        <span>{`${line1 ?? ""} ${line2 ?? ""}`}</span>
        <span>{`${postalCode ?? ""} ${province ?? ""}`}</span>
        <span>{country && <Trans i18nKey={country} />}</span>
      </LinkButton>
    );
  };

  const checkInvalidDates = () => {
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");
    if (startDate && endDate) {
      return startDate < endDate || ErrorType.DATE;
    }
  };

  const checkInvalidQuantity = () => {
    if (!selectedProduct) {
      return true;
    }
    return watch("totalQuantity")! % watch("packagingOption")! === 0 || ErrorType.QUANTITY;
  };

  return (
    <form onSubmit={submitHandler}>
      {addressModal && (
        <Modal title={t("newContract.selectShippingAddress")} onClose={hideAddressModal}>
          <div className={classes.addressModal}>
            <span className={classes.modalLabel}>{t("newContract.defaultShippingAddress")}</span>
            {DEFAULT_ADDRESS && (
              <AddressInformation
                onClick={() => {
                  changeSelectedAddress(DEFAULT_ADDRESS);
                  hideAddressModal();
                }}
                id={DEFAULT_ADDRESS?.id}
                company={DEFAULT_ADDRESS?.companyName}
                line1={DEFAULT_ADDRESS.line1 ?? ""}
                line2={DEFAULT_ADDRESS.line2 ?? ""}
                postalCode={DEFAULT_ADDRESS?.postalCode}
                province={DEFAULT_ADDRESS?.province}
                country={DEFAULT_ADDRESS?.country.name}
                className={classes.modalAddress}
              />
            )}
            <span className={classNames("pt-2", classes.modalLabel)}>{t("newContract.accountApprovedAddresses")}</span>
            {parsedAddresses
              .filter((address) => !address.defaultAddress)
              .map((address) => (
                <AddressInformation
                  onClick={() => {
                    changeSelectedAddress(address);
                    hideAddressModal();
                  }}
                  id={address?.id}
                  company={address?.companyName}
                  line1={address.line1 ?? ""}
                  line2={address.line2 ?? ""}
                  postalCode={address?.postalCode}
                  province={address?.province}
                  country={address?.country.name}
                  className={classes.modalAddress}
                />
              ))}
          </div>
        </Modal>
      )}
      <div className={classes.container}>
        <PageTitle>{t("newContract.title")}</PageTitle>
        <div className={classes.tilesContainer}>
          <Tile title={t("newContract.contractTile")}>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <MaterialInput
                  error={!!error}
                  value={value}
                  onChange={onChange}
                  helperLabel={false}
                  label={t("contractOverview.table.contractNo") + "*"}
                  startAdornment={error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
                  placeholder={error && t("newContract.contractErrorPlaceholder")}
                  inputProps={{
                    "clta-new-contract-page-element-marker": "contract-id-input",
                  }}
                />
              )}
              name="contractNumber"
              control={control}
              rules={{ required: true }}
            />

            <div className={classes.dateContainer}>
              <Controller
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DateInput
                    date={value}
                    onChange={(newDate) => {
                      onChange(newDate);
                      trigger(["startDate", "endDate"]);
                    }}
                    minYear={new Date().getFullYear()}
                    placeholder={t("newContract.startDate") + "*"}
                    helperLabel={
                      <span className={error && classes.errorLabel}>{t("bulkUpload.help.example.dateFormat")}</span>
                    }
                    label={value ? t("newContract.startDate") + "*" : true}
                    isInvalid={!!error}
                    isDayDisabled={isDateInvalidForDelivery}
                    startAdornment={error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
                  />
                )}
                name="startDate"
                control={control}
                rules={{
                  required: true,
                  validate: checkInvalidDates,
                }}
              />
              <Controller
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DateInput
                    date={value}
                    onChange={(newDate) => {
                      onChange(newDate);
                      trigger(["startDate", "endDate"]);
                    }}
                    placeholder={t("newContract.endDate") + "*"}
                    helperLabel={
                      <span className={error && classes.errorLabel}>{t("bulkUpload.help.example.dateFormat")}</span>
                    }
                    label={value ? t("newContract.endDate") + "*" : true}
                    isInvalid={!!error}
                    isDayDisabled={isDateInvalidForDelivery}
                    startAdornment={error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
                  />
                )}
                name="endDate"
                control={control}
                rules={{
                  required: true,
                  validate: checkInvalidDates,
                }}
              />
            </div>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <QuantityInput
                  value={value}
                  helperLabel={false}
                  onValueChange={(newValue) => {
                    onChange(newValue);
                    if (selectedProduct) {
                      trigger("packagingOption");
                    }
                  }}
                  className={classes.quantityInput}
                  step={1}
                  min={1}
                  startAdornment={error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
                  label={t("contractDetails.totals.totalQty") + "*"}
                  error={!!error}
                />
              )}
              name="totalQuantity"
              control={control}
              rules={{ required: true, validate: checkInvalidQuantity }}
            />
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <QuantityInput
                  value={value}
                  helperLabel={false}
                  onValueChange={onChange}
                  className={classes.quantityInput}
                  step={1}
                  min={1}
                  startAdornment={error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
                  label={t("newContract.numberOfDeliveries") + "*"}
                  error={!!error}
                />
              )}
              name="numberOfDeliveries"
              control={control}
              rules={{ required: true }}
            />
          </Tile>
          <Tile title={t("orderDetailsPage.table.header.product")}>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <AutosuggestProductInput
                  {...inputProps}
                  label={t("bulkUpload.table.header.productId") + "*"}
                  inputValue={value ?? ""}
                  onInputChange={(event, selectedValue) => {
                    setPackageOptions(undefined);
                    onChange(selectedValue);
                  }}
                  products={products}
                  error={!!error}
                  startAdornment={error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
                  inputProps={{
                    "clta-new-contract-page-element-marker": "product-id-input",
                  }}
                />
              )}
              name="productId"
              control={control}
              rules={{ required: true }}
            />

            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SimpleSelect
                  value={value}
                  className={classes.enforceSolidBorder}
                  onChange={(chosenSize) => {
                    onChange(chosenSize);
                    if (selectedProduct) {
                      trigger("totalQuantity");
                    }
                  }}
                  fullWidth
                  InputProps={{
                    startAdornment: error && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />,
                  }}
                  label={t("addToCart.contract.packaging") + "*"}
                  values={
                    packageOptions?.packagingInformation.map((entry) => ({
                      label: t("addToCartPopup.packSize", {
                        amount: entry.packSize,
                        code: entry.packCode,
                      }),
                      value: entry.packSize,
                    })) ?? []
                  }
                  error={!!error}
                  disabled={!packageOptions?.packagingInformation}
                />
              )}
              name="packagingOption"
              control={control}
              rules={{
                required: true,
                validate: checkInvalidQuantity,
              }}
            />
          </Tile>
          <Tile title={t("newContract.shippingTile")}>
            <div className={classes.addressContainer}>
              <div className={classes.addressLabel}>
                <span>{t("checkoutPage.headers.shippingAddress")}</span>
                <IconClickable
                  onClick={showAddressModal}
                  clickableType={IconClickableType.PRIMARY}
                  type={IconType.Pencil}
                />
              </div>
              <div className={classes.addressInformation}>
                <span className={classes.selectedPartner}>{watch("shippingCompany")}</span>
                <span>{`${watch("shippingAddressLine1") ?? ""} ${watch("shippingAddressLine2") ?? ""}`}</span>
                <span>{`${watch("shippingAddressPostalCode") ?? ""} ${watch("shippingAddressProvince") ?? ""}`}</span>
                <span>
                  <Trans i18nKey={watch("shippingAddressCountry")} />
                </span>
              </div>
            </div>
            {filteredUnloadingPoints && filteredUnloadingPoints?.length > 0 && (
              <Controller
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <SimpleSelect
                    value={value}
                    onChange={onChange}
                    fullWidth
                    label={
                      <div className={classes.inputLabel}>
                        {error && <Icon type={IconType.ErrorCircle} />}
                        <span>{t("checkoutPage.headers.unloading") + "*"}</span>
                      </div>
                    }
                    values={filteredUnloadingPoints.map((point) => ({ label: point.name, value: point.name })) ?? []}
                    error={!!error}
                  />
                )}
                name="unloadingPoint"
                control={control}
                rules={{ required: filteredUnloadingPoints?.length > 0 ? true : false }}
              />
            )}
          </Tile>
          <div className={classes.commentContainer}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <TextField
                  variant={"outlined"}
                  multiline
                  fullWidth
                  inputProps={{ sx: { height: "170px !important" } }}
                  value={value}
                  onChange={onChange}
                  minRows={"9"}
                  placeholder={t("newContract.commentPlaceholder")}
                  hidden={false}
                  className={classes.commentInput}
                />
              )}
              name="comment"
              control={control}
            />

            <DynamicErrorMessage errors={formState.errors} />
          </div>
        </div>
        <div>{`*${t("checkoutPage.addressModal.mandatory")}`}</div>
        <div className={classes.actionsContainer}>
          <span className={classes.clearAll}>
            <Button onClick={() => reset(EMPTY_VALUES)} startIcon={<Icon type={IconType.Reset} />} variant="text">
              {t("searchPage.refinement.button.clearAll")}
            </Button>
          </span>
          <Button
            type="submit"
            className={classes.submitButton}
            clta-new-contract-page-element-marker="request-new-contract-button"
          >
            {t("newContract.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
};

interface Props {
  addresses: string;
  soldTo: string;
}

export function createRequestContractPage(data: Props) {
  return <RequestContractPage {...data} />;
}
